import { useState, useCallback, useEffect } from "react";
import { Loader2, Pencil, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AISparkles } from "@/components/ui/ai-sparkles";
import { useZoneAlternatives } from "@/resources/ab-test/ab-test.api";
import { AXIS_LABEL } from "@/constants/ab-tests";
import type { AxisKey, ZoneAlternative } from "@/types/ab-tests";
import { CustomAlternativeInput } from "./CustomAlternativeInput";
import { KeywordPicker } from "./KeywordPicker";

interface AlternativesPanelProps {
  axis: AxisKey;
  zoneText: string;
  productNameAlsoCarries: AxisKey[];
  brandName: string;
  productName: string;
  fieldLabel: string;
  fieldText: string;
  masterKeywords: string[];
  keywordKpsScores?: Record<string, number>;
  productId: number;
  brandId: number;
  onSelect: (alt: ZoneAlternative) => void;
  onPickKeyword?: (keyword: string) => void;
  keywordIntegrationSlot?: React.ReactNode;
  selectedKeyword?: string | null;
  onResetKeyword?: () => void;
  onClose: () => void;
}

export function AlternativesPanel({
  axis,
  zoneText,
  productNameAlsoCarries,
  fieldLabel,
  fieldText,
  masterKeywords,
  keywordKpsScores,
  productId,
  brandId,
  onSelect,
  onPickKeyword,
  keywordIntegrationSlot,
  selectedKeyword,
  onResetKeyword,
}: AlternativesPanelProps) {
  const [alts, setAlts] = useState<ZoneAlternative[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [editingText, setEditingText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const zoneAltsMutation = useZoneAlternatives();
  const productNameCoversAxis = productNameAlsoCarries.includes(axis);

  const fetchAlts = useCallback(async () => {
    setError(null);
    setSelectedIndex(null);
    const kwList = Array.from(new Set(masterKeywords)).slice(0, 60);
    try {
      const res = await zoneAltsMutation.mutateAsync({
        productId,
        brandId,
        fieldLabel,
        fieldText,
        zoneText,
        axis,
        alsoCarries: productNameAlsoCarries,
        availableKeywords: kwList,
      });
      const list = Array.isArray(res?.alternatives) ? res.alternatives : [];
      setAlts(
        list
          .filter((a) => a && typeof a.text === "string" && a.text.trim())
          .slice(0, 3)
          .map((a) => ({
            text: a.text.trim(),
            angle: typeof a.angle === "string" ? a.angle : "",
            used_keywords: Array.isArray(a.used_keywords)
              ? a.used_keywords.filter((k): k is string => typeof k === "string" && !!k.trim())
              : [],
          })),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch alternatives.");
    }
  }, [axis, fieldLabel, fieldText, zoneText, productNameAlsoCarries, masterKeywords, productId, brandId, zoneAltsMutation]);

  useEffect(() => {
    fetchAlts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loading = zoneAltsMutation.isPending;

  return (
    <div className="space-y-7">
      <section className="space-y-2.5">
        <div className="space-y-1">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-1.5">
              <AISparkles className="h-3.5 w-3.5" />
              <h5 className="text-base font-semibold leading-6 text-foreground">Signal AI Alternatives</h5>
            </div>
            {!error && (
              <Button type="button" variant="ghost" size="sm" onClick={fetchAlts} disabled={loading} className="h-7 px-2 -my-1">
                <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
                Regenerate
              </Button>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground leading-snug">Based on the COSMOS framework.</p>
          {productNameCoversAxis && (
            <p className="text-[11px] text-muted-foreground leading-snug">
              Product Name already establishes {AXIS_LABEL[axis].toLowerCase()}. Alternatives add depth instead of restating it.
            </p>
          )}
        </div>

        {loading && (
          <div className="flex items-center gap-2 py-3 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Generating phrasings…
          </div>
        )}

        {error && (
          <div className="text-xs text-destructive flex items-center justify-between gap-2">
            <span>{error}</span>
            <Button type="button" variant="ghost" size="sm" onClick={fetchAlts}>Retry</Button>
          </div>
        )}

        {!loading && !error && alts.length > 0 && (
          <>
            <ul className="space-y-1.5">
              {alts.map((alt, i) => {
                const isSelected = selectedIndex === i;
                return (
                  <li key={i}>
                    {isSelected && editingText !== null ? (
                      <div className="w-full rounded-md border border-primary bg-primary/5 px-2.5 py-2">
                        <Textarea autoFocus value={editingText} onChange={(e) => setEditingText(e.target.value)} rows={3} className="text-sm leading-snug" />
                        {alt.angle && <p className="text-[11px] text-muted-foreground italic mt-1">{alt.angle}</p>}
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => { setSelectedIndex(isSelected ? null : i); setEditingText(null); }}
                        aria-pressed={isSelected}
                        className={cn(
                          "w-full text-left rounded-md border px-2.5 py-2 transition-colors",
                          isSelected ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary hover:bg-primary/5",
                        )}
                      >
                        <p className="text-sm text-foreground leading-snug">{alt.text}</p>
                        {alt.angle && <p className="text-[11px] text-muted-foreground italic mt-0.5">{alt.angle}</p>}
                        {alt.used_keywords && alt.used_keywords.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {alt.used_keywords.map((kw) => (
                              <span key={kw} className="inline-flex items-center rounded-full bg-status-info-soft text-foreground text-[10px] px-1.5 py-px font-medium">
                                kw: {kw}
                              </span>
                            ))}
                          </div>
                        )}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
            {selectedIndex !== null && (
              <div className="flex items-center justify-end gap-2 pt-1">
                <Button type="button" variant="outline" size="sm" onClick={() => { setSelectedIndex(null); setEditingText(null); }}>Cancel</Button>
                {editingText === null ? (
                  <Button type="button" variant="outline" size="sm" onClick={() => setEditingText(alts[selectedIndex].text)}>
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </Button>
                ) : null}
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    const base = alts[selectedIndex];
                    const finalText = (editingText ?? base.text).trim();
                    if (!finalText) return;
                    onSelect({ ...base, text: finalText });
                  }}
                >
                  Save
                </Button>
              </div>
            )}
          </>
        )}
      </section>

      {masterKeywords.length > 0 && (
        <KeywordPicker
          keywords={masterKeywords}
          kpsScores={keywordKpsScores}
          onSelect={(kw) => onPickKeyword ? onPickKeyword(kw) : onSelect({ text: kw, angle: "From keyword list" })}
          integrationSlot={keywordIntegrationSlot}
          selectedKeyword={selectedKeyword ?? null}
          onResetKeyword={onResetKeyword}
        />
      )}

      <CustomAlternativeInput
        axis={axis}
        onSubmit={(text) => onSelect({ text, angle: "Custom phrasing" })}
      />
    </div>
  );
}
